import Head from 'next/head';
import { useIntl, type IntlShape } from 'react-intl';

import type {
  GetEventSalesStatusResponse,
  WebappResponse,
  OfferSchema,
} from '@eventbrite/event-listing-service-client';

import type { BasicInfo, Gallery, StructuredContent, Tags, Venue } from '@/types';
import { getFormattedEndDate, getFormattedStartDate } from '@/utils/dateTime';
import { formatLocale } from '../../../i18n/locale';
import { resolveListingCanonicalUrl } from '@/utils/seriesChildListingSeo';
import { ArtistInfo } from '../ArtistsInfo/ArtistsInfo';
import { preparePlaceholders } from '../EventTags/utils/localizedTagsUtils';

const BASE_JSON_LD_DATA = {
  '@context': 'https://schema.org',
};

const EVENT_FORMATS: Record<string, { label: string; schema: string }> = {
  '1': { label: 'Conference', schema: 'BusinessEvent' },
  '2': { label: 'Seminar', schema: 'EducationEvent' },
  '3': { label: 'Expo', schema: 'BusinessEvent' },
  '4': { label: 'Convention', schema: 'BusinessEvent' },
  '5': { label: 'Festival', schema: 'Festival' },
  '6': { label: 'Performance', schema: 'Event' },
  '7': { label: 'Screening', schema: 'Event' },
  '8': { label: 'Gala', schema: 'SocialEvent' },
  '9': { label: 'Class', schema: 'EducationEvent' },
  '10': { label: 'Networking', schema: 'SocialEvent' },
  '11': { label: 'Party', schema: 'SocialEvent' },
  '12': { label: 'Rally', schema: 'SportsEvent' },
  '13': { label: 'Tournament', schema: 'SportsEvent' },
  '14': { label: 'Game', schema: 'SocialEvent' },
  '15': { label: 'Race', schema: 'SportsEvent' },
  '16': { label: 'Tour', schema: 'MusicEvent' },
  '17': { label: 'Attraction', schema: 'Event' },
  '18': { label: 'Retreat', schema: 'SocialInteraction' },
  '19': { label: 'Appearance', schema: 'Event' },
  '100': { label: 'Other', schema: 'Event' },
};

const getListingPageUrl = (basicInfo: BasicInfo) => resolveListingCanonicalUrl(basicInfo) ?? basicInfo.url;

const getWebPageData = (basicInfo: BasicInfo) => {
  return {
    ...BASE_JSON_LD_DATA,
    '@type': 'WebPage',
    name: `${basicInfo.name}`,
    speakable: {
      '@type': 'SpeakableSpecification',
      xPath: ["/html/head/meta[@property='og:title']/@content", "/html/head/meta[@name='description']/@content"],
    },
    url: getListingPageUrl(basicInfo),
  };
};
const getFAQData = (structuredContent?: StructuredContent) => {
  if (structuredContent?.widgets?.faqs && structuredContent?.widgets?.faqs.length > 0) {
    const mainEntity = structuredContent.widgets.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    }));
    return {
      ...BASE_JSON_LD_DATA,
      '@type': 'FAQPage',
      mainEntity,
    };
  }
  return null;
};

const getBreadCrumbsData = (tags?: Tags, intl?: IntlShape) => {
  if (tags?.length && tags.length > 0 && intl) {
    const itemListElement = tags.map((tag, index) => {
      let name = tag.text.text;
      if (tag.text.isLocalized) {
        const placeHolders = preparePlaceholders(tag.text.placeholders, intl);
        name = intl.formatMessage({ id: tag.text.text }, placeHolders);
      }

      const linkWithTrailingSlash = tag.link.endsWith('/') ? tag.link : `${tag.link}/`;

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': linkWithTrailingSlash,
          name: name,
        },
      };
    });

    return {
      ...BASE_JSON_LD_DATA,
      '@type': 'BreadcrumbList',
      itemListElement,
    };
  }
  return null;
};

const getVideoObjectData = (gallery: Gallery, basicInfo: BasicInfo) => {
  const nativeContentUrl = gallery?.nativeVideo?.url;
  const nativeThumbnailUrl = gallery?.nativeVideo?.thumbnail;

  if (nativeContentUrl && nativeThumbnailUrl) {
    return {
      ...BASE_JSON_LD_DATA,
      '@type': 'VideoObject',
      name: basicInfo.name,
      description: basicInfo.summary,
      contentUrl: nativeContentUrl,
      uploadDate: basicInfo.created,
      thumbnailUrl: nativeThumbnailUrl,
    };
  }

  const featuredEmbedUrl = gallery?.featuredVideo?.embedUrl;
  const featuredContentUrl = gallery?.featuredVideo?.url;
  const featuredThumbnailUrl = gallery?.featuredVideo?.thumbnail?.large?.url;

  if (featuredEmbedUrl && featuredThumbnailUrl) {
    return {
      ...BASE_JSON_LD_DATA,
      '@type': 'VideoObject',
      name: basicInfo.name,
      description: basicInfo.summary,
      embedUrl: featuredEmbedUrl,
      ...(featuredContentUrl && { contentUrl: featuredContentUrl }),
      uploadDate: basicInfo.created,
      thumbnailUrl: featuredThumbnailUrl,
    };
  }

  return null;
};

const getEventType = (formatId?: string) => {
  if (formatId && EVENT_FORMATS[formatId]) {
    return EVENT_FORMATS[formatId].schema;
  }
  return 'Event';
};

const getEventStatus = (salesStatus?: GetEventSalesStatusResponse) => {
  //Possible eventStatus values. Currently we only have statuses of EventCancelled, EventPostponed, and EventScheduled
  // on the site so we'll only use those three for the timebeing
  const EVENT_CANCELLED = 'https://schema.org/EventCancelled';
  const EVENT_POSTPONED = 'https://schema.org/EventPostponed';
  const EVENT_SCHEDULED = 'https://schema.org/EventScheduled';

  if (salesStatus && salesStatus.messageCode === 'event_cancelled') {
    return EVENT_CANCELLED;
  }
  if (salesStatus && salesStatus.messageCode === 'event_postponed') {
    return EVENT_POSTPONED;
  }
  return EVENT_SCHEDULED;
};

const getPostalAddressData = (address?: Venue['address']) => {
  if (address)
    return {
      '@type': 'PostalAddress',
      ...(address.city && { addressLocality: address.city }),
      ...(address.region && { addressRegion: address.region }),
      ...(address.country && { addressCountry: address.country }),
      ...(address.localizedMultiLineAddressDisplay && {
        streetAddress: address.localizedMultiLineAddressDisplay.join(', '),
      }),
    };
  return null;
};

const getEventLocation = (basicInfo: BasicInfo) => {
  if (basicInfo.isOnline) {
    return {
      '@type': 'VirtualLocation',
      url: getListingPageUrl(basicInfo),
    };
  }
  const addressData = getPostalAddressData(basicInfo.venue?.address);
  return {
    '@type': 'Place',
    name: basicInfo.venue?.name || 'TBD',
    ...(addressData && { address: addressData }),
  };
};

const getOrganizerData = (basicInfo: BasicInfo) => {
  return {
    '@type': 'Organization',
    name: basicInfo.organizer?.name,
    ...(basicInfo.organizer?.url && { url: basicInfo.organizer?.url }),
    ...(basicInfo.organizer?.description && { description: basicInfo.organizer?.description }),
  };
};

const getPerformerData = (artistInfo?: ArtistInfo) => {
  if (artistInfo?.artists?.length && artistInfo.artists.length > 0) {
    return artistInfo.artists.map((artist) => ({
      '@type': 'Person',
      name: artist.name,
      ...(artist.socialMedia?.length && { url: artist.socialMedia[0].url }),
    }));
  }
  return null;
};

const getOffersData = (offersSchema: OfferSchema[]) => {
  return offersSchema.map((offer) => ({
    '@type': 'AggregateOffer',
    lowPrice: offer.lowPrice,
    highPrice: offer.highPrice,
    url: offer.url,
    availability: offer.availability,
    ...(offer.availabilityStarts && { availabilityStarts: offer.availabilityStarts }),
    ...(offer.availabilityEnds && { availabilityEnds: offer.availabilityEnds }),
    ...(offer.validFrom && { validFrom: offer.validFrom }),
    ...(offer.priceCurrency && { priceCurrency: offer.priceCurrency }),
  }));
};

const getEventData = ({
  basicInfo,
  artistInfo,
  primaryImageUrl,
  salesStatus,
  seo,
  locale,
}: {
  basicInfo: BasicInfo;
  artistInfo?: ArtistInfo;
  primaryImageUrl?: string;
  salesStatus?: GetEventSalesStatusResponse;
  seo?: WebappResponse['seo'];
  locale?: string;
}) => {
  const startDateFormatted = getFormattedStartDate(basicInfo);
  const endDateFormatted = getFormattedEndDate(basicInfo);
  const performerData = getPerformerData(artistInfo);
  const offersData = seo?.offersSchema?.length ? getOffersData(seo.offersSchema) : null;

  return {
    ...BASE_JSON_LD_DATA,
    '@type': getEventType(basicInfo.formatId),
    name: basicInfo.name,
    description: basicInfo.summary,
    url: getListingPageUrl(basicInfo),
    image: primaryImageUrl,
    eventStatus: getEventStatus(salesStatus),
    location: getEventLocation(basicInfo),
    organizer: getOrganizerData(basicInfo),
    eventAttendanceMode: basicInfo.isOnline
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    ...(locale && { inLanguage: locale }),
    ...(startDateFormatted && { startDate: startDateFormatted }),
    ...(endDateFormatted && { endDate: endDateFormatted }),
    ...(performerData && { performer: performerData }),
    ...(offersData && { offers: offersData }),
  };
};

interface JSONLDTagsProps {
  basicInfo: BasicInfo;
  gallery: Gallery;
  artistInfo?: ArtistInfo;
  primaryImageUrl?: string;
  seo: WebappResponse['seo'];
  salesStatus?: GetEventSalesStatusResponse;
  structuredContent?: StructuredContent;
  tags?: Tags;
}

export const JSONLDTags = ({
  basicInfo,
  seo,
  artistInfo,
  gallery,
  primaryImageUrl,
  salesStatus,
  structuredContent,
  tags,
}: JSONLDTagsProps) => {
  const intl = useIntl();
  const schemaLocale = formatLocale(intl.locale);
  const jsonLDData = {
    'web-page-data': getWebPageData(basicInfo),
    'event-data': getEventData({ basicInfo, artistInfo, primaryImageUrl, seo, salesStatus, locale: schemaLocale }),
    'faq-data': getFAQData(structuredContent),
    'breadcrumbs-data': getBreadCrumbsData(tags, intl),
    'video-object-data': getVideoObjectData(gallery, basicInfo),
  };
  return (
    <Head>
      {Object.entries(jsonLDData)
        .filter(([, data]) => !!data)
        .map(([key, data]) => (
          <script key={key} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
        ))}
    </Head>
  );
};
